import { type CallhomeAiTag } from '@solinkcorp/api-types';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { GripVertical, Settings, Trash2 } from 'lucide-react';
import React, { useId } from 'react';
import { ICON_SIZE } from '../../constants';
import WidgetTagBadges from './WidgetTagBadges';

interface WidgetHeaderProps {
  title: string;
  /** Render the title in the error colour. Ignored in overlay mode, where the title stays white. */
  titleError?: boolean;
  /** Tags this widget draws data from. Only shown while editing the layout. */
  tags: readonly CallhomeAiTag[];
  editing: boolean | undefined;
  isDragging?: boolean;
  /** Float over the widget's content instead of reserving a row above it. */
  overlay?: boolean;
  onConfigure: () => void;
  onDelete: () => void;
}

const neutralIconButtonStyles =
  'transition-colors hover:bg-interaction-neutral-background-hover hover:text-interaction-neutral-content-default data-[dragging=true]:bg-interaction-neutral-background-hover data-[dragging=true]:text-interaction-neutral-content-default';

/**
 * The title takes the larger size in view mode, where the grip, tag badges and action buttons
 * aren't competing with it for the row.
 */
const TITLE_FONT_SIZE = {
  editing: 'clamp(0.5rem, 2vh, 1rem)',
  viewing: 'clamp(0.625rem, 2vh, 1.25rem)',
};

/**
 * The widget's top row: title and tags on the left, edit affordances on either side of them.
 *
 * The grip only exists while editing, so the title sits flush left the rest of the time. The
 * action buttons keep their space either way, which holds the row's height steady across modes.
 */
const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  title,
  titleError,
  tags,
  editing,
  isDragging,
  overlay,
  onConfigure,
  onDelete,
}) => {
  const widgetHeaderId = useId();

  return (
    <>
      {/* Scrim for legibility over footage. Sized against the card rather than the row so it
          fades out 30% of the way down the frame, per design. The start colour is spelled out
          because the preset maps `black` to a `var()`, which Tailwind can't apply a `/80`
          opacity modifier to — `from-black/80` silently compiles to nothing. */}
      {overlay && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[30%] bg-gradient-to-b from-[rgb(0_0_0_/_0.8)] to-transparent"
        />
      )}

      <div
        className={cn(
          // Left padding exceeds the top padding so the glyphs look equally inset on both
          // edges: the line box already adds space above the text, but none to its left.
          'z-10 flex w-full shrink-0 items-center gap-1.5 pl-3 pr-1.5 pt-1.5',
          overlay ? 'absolute left-0 top-0 text-white' : 'relative',
        )}
      >
        {/* Visual grip only — dnd-kit listeners live on the widget card wrapper. */}
        {editing && (
          <Button
            variant="ghost"
            size="icon-xs"
            tabIndex={-1}
            aria-hidden
            data-testid="sdm-widget-drag-handle"
            data-dragging={isDragging || undefined}
            className={cn(neutralIconButtonStyles, 'shrink-0')}
          >
            <GripVertical size={ICON_SIZE} />
          </Button>
        )}

        <div
          className={cn(
            'truncate text-left font-bold',
            overlay ? 'text-white' : titleError ? 'text-text-error' : 'text-foreground-secondary',
          )}
          style={{ fontSize: editing ? TITLE_FONT_SIZE.editing : TITLE_FONT_SIZE.viewing }}
          data-testid="sdm-widget-title"
        >
          {title}
        </div>

        {editing && <WidgetTagBadges tags={tags} />}

        <div
          className={cn(
            'ml-auto flex shrink-0',
            editing ? 'visible opacity-100' : 'invisible opacity-0',
          )}
        >
          <Button
            onClick={onConfigure}
            variant="ghost"
            size="icon"
            className="w-8 px-5 py-2.5"
            data-testid={`sdm-edit-widget-button-${widgetHeaderId}`}
          >
            <Settings
              size={ICON_SIZE}
              className={overlay ? 'text-white' : 'text-interaction-neutral-content-default'}
            />
          </Button>
          <Button
            onClick={onDelete}
            variant="ghost"
            size="icon"
            className="ml-2 w-8 px-5 py-2.5 hover:bg-destructive/10"
            data-testid={`sdm-delete-widget-button-${widgetHeaderId}`}
          >
            <Trash2 size={ICON_SIZE} className="text-destructive" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default WidgetHeader;
